import React, { useEffect, useState } from 'react'
import RestApi from '../Apis/RestApi';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';

function ChangePassword() {
  const location = useLocation();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newConfirmPassword, setNewConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isGoogleUser, setIsGoogleUser] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const params=useParams();
  
  const userid = localStorage.getItem("_id") || 
               location.state?.user_id || 
               new URLSearchParams(window.location.search).get('user_id') || 
               params.userId;
  const [urlState, setUrlState] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    // Check if this is a password reset URL
    if (window.location.href.includes("change-password")) {
      setUrlState(true);
    } else {
      setUrlState(false);
    }
    
    // Check if user logged in with Google and it's first login
    const checkUserStatus = async () => {
      try {
        // Get user data from localStorage and parse it properly
        const userDataString = localStorage.getItem("user");
        
        if (userDataString) {
          // Parse the JSON string to an object
          try {
            const userData = JSON.parse(userDataString);
            
            // Check if signupType exists in the parsed object
            if (userData && userData.signupType) {
              setIsGoogleUser(userData.signupType === 'Google');
            }
          } catch (parseError) {
            console.error("Error parsing user data:", parseError);
            // If it's not valid JSON, it might be directly the signup type
            setIsGoogleUser(userDataString === 'Google');
          }
        }
        
        const firstLogin = localStorage.getItem("first_login");
        setIsFirstLogin(firstLogin === 'true');
      } catch (error) {
        console.error("Error checking user status:", error);
      }
    };
    
    checkUserStatus();
  }, []);

  const handleChangePassword = async () => {
    // Validate password length
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    // Confirm passwords match
    if (newConfirmPassword !== newPassword) {
      toast.error('Password and Confirm Password must be same');
      return;
    }
    
    setLoading(true);
    
    try {
      let response;
      
      if (urlState) {
        // This is a password reset from email link
        const data = {
          newpassword: newPassword,
          user_id: userid
        };
        response = await RestApi.resetPassword(data);
      } else if (isGoogleUser && isFirstLogin) {
        // This is a first-time Google login (no old password needed)
        const data = {
          _id: userid,
          new_password: newPassword
        };
        response = await RestApi.changePassword(data);
      } else {
        // Regular password change (requires old password)
        const data = {
          old_password: oldPassword,
          _id: userid,
          new_password: newPassword
        };
        response = await RestApi.changePassword(data);
      }
      
      if (response.data.status) {
        toast.success(response.data.msg);
        localStorage.setItem("first_login", "false");
        
        // Redirect based on context
        if (urlState) {
          navigate('/login');
        } else if (isGoogleUser && isFirstLogin) {
          navigate('/home');
        }
      } else {
        toast.error(response.data.msg || "Unable to change password");
      }
    } catch (error) {
      console.error("Password change error:", error);
      if (error.response?.data?.msg) {
        toast.error(error.response.data.msg);
        setError(error.response.data.msg);
      } else {
        toast.error("An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (urlState) {
      navigate('/login');
    } else if (isGoogleUser) {
      // If it's first login, we don't want to go back, as they need to set a password
      navigate('/home');
    } else {
      // Regular back - could be to settings page or previous page
      navigate(-1);
    }
  };

  return (
    <div className={`${urlState ? 'flex justify-center items-center bg-gray-700 max-w-full h-screen' : 'container mx-auto'}`}>
      <div className="max-w-sm mx-auto mt-10">
        {/* Back button */}
        <div className="flex items-center mb-6">
          <button 
            onClick={handleBack}
            className="mr-2 p-2 rounded-full hover:bg-gray-700 transition-colors duration-200"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5 text-white" />
          </button>
          <h2 className="text-2xl font-bold">
            {urlState 
              ? "Reset Password" 
                : "Change Password"
            }
          </h2>
        </div>

        <div className='bg-gray-600 p-4 rounded-lg'>
          {/* Only show old password field for regular password changes */}
          {!urlState && !(isGoogleUser) && (
            <input
              type="password"
              placeholder="Old Password"
              className="border p-2 w-full mb-4 text-black rounded-md"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              required
            />
          )}
          
          <input
            type="password"
            placeholder="New Password"
            className="border p-2 w-full mb-4 text-black rounded-md"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          
          <input
            type="password"
            placeholder="Confirm New Password"
            className="border p-2 w-full mb-4 text-black rounded-md"
            value={newConfirmPassword}
            onChange={(e) => setNewConfirmPassword(e.target.value)}
            required
          />
          
          <button
            className="bg-[#126f97] text-white p-2 w-full rounded-md disabled:opacity-50"
            onClick={handleChangePassword}
            disabled={loading}
          >
            {loading 
              ? "Processing..." 
              : urlState 
                ? "Reset Password" 
                : isGoogleUser && isFirstLogin 
                  ? "Set Password" 
                  : "Change Password"
            }
          </button>
          
          {error && <p className="text-red-500 mt-2">{error}</p>}
          
          {isGoogleUser && isFirstLogin && (
            <p className="text-sm mt-4 text-gray-300">
              Since you signed up with Google, you need to set a password to fully secure your account.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default ChangePassword